import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
	Button,
	ButtonStack,
	createCSV,
	Drawer,
	FilterFields,
	Heatmap,
	heatmapColorSchemes,
	PageHeading,
	PrintableReportHeader,
	Select,
	Tooltip,
} from 'components';
import { useAppSelector, useGetRoomsRunningQuery, useSystem } from 'store';
import { LoadingIndicator } from 'components/Select/subcomponents';
import { useFilters } from 'context';

function HeatmapPage() {
	const { selectedFacility, selectedSystem } = useAppSelector((state) => state.userState);
	const { data } = useSystem();
	const current_facility = data?.facilities?.find((f) => f.id === selectedFacility);
	const system = data?.healthsystems.find((h) => h.id === selectedSystem);
	const facilityName = current_facility?.intraop_facility_name_alias
		? current_facility.intraop_facility_name_alias
		: current_facility?.name;
	const has_scheduled = current_facility?.has_scheduled;

	const { dateRange, clearFilters, metadata, resetFilters, filtersAreDirty, applyFilters } = useFilters();
	const [showHeatmap, setShowHeatmap] = useState<string[]>(has_scheduled ? ['actual', 'scheduled'] : ['actual']);
	const [exportCsv, setExportCsv] = useState(false);
	const [printing, setPrinting] = useState(false);

	const updateShowHeatmap = (option: string) => {
		if (!showHeatmap.includes(option)) {
			setShowHeatmap([...showHeatmap, option]);
		} else {
			setShowHeatmap(showHeatmap.filter((item) => item !== option));
		}
	};

	const styleMap = {
		actual: {
			title: 'Actual Cases',
			colorScheme: 'viridis',
		},
		predicted: {
			title: 'Predicted Cases',
			colorScheme: 'blueish',
		},
		difference: {
			title: 'Scheduled/Actual Difference',
			colorScheme: 'rocket',
		},
		scheduled: {
			title: 'Scheduled Cases',
			colorScheme: 'plasma',
		},
	};

	const { dropDowns, saveDropdown, heatmapMethod } = useFilters();

	const concurrent_view_by_options = [
		{ value: 'day_of_week', label: 'Day of Week' },
		{ value: 'facility_name', label: 'Facility (For Combined Facilities)' },
		{ value: 'serviceline_name', label: 'Service Line' },
		{ value: 'encountertype_name', label: 'Encounter Type' },
		{ value: 'serviceline_name_dow', label: 'Service Line & DOW' },
		{ value: 'encountertype_name_dow', label: 'Encounter Type & DOW' },
		{ value: 'facility_name_dow', label: 'Facility & DOW (For Combined Facilities)' },
	];

	const cumulative_view_by_options = [
		{ value: 'day_of_week', label: 'Day of Week' },
		{ value: 'surgeon_name', label: 'Surgeon' },
		{ value: 'room_name', label: 'Room' },
		{ value: 'facility_name', label: 'Facility (For Combined Facilities)' },
		{ value: 'serviceline_name', label: 'Service Line' },
		{ value: 'encountertype_name', label: 'Encounter Type' },
		{ value: 'surgeon_name_dow', label: 'Surgeon & DOW' },
		{ value: 'room_name_dow', label: 'Room & DOW' },
		{ value: 'serviceline_name_dow', label: 'Service Line & DOW' },
		{ value: 'encountertype_name_dow', label: 'Encounter Type & DOW' },
		{ value: 'facility_name_dow', label: 'Facility & DOW (For Combined Facilities)' },
	];

	const in_concurrent_mode = heatmapMethod.applied.id < 4;

	// Generate PDF from the current page, following the Analytics export approach.
	// If the captured report is taller than one PDF page, slice the same canvas
	// across as many PDF pages as needed instead of squeezing/clipping it.
	const generatePDF = async () => {
		const element = document.getElementById('overview');

		if (!element) {
			setPrinting(false);
			return;
		}

		try {
			const doc = new jsPDF();

			const html2canvasOptions = {
				useCORS: true,
				allowTaint: true,
				dpi: 200,
				scrollY: -window.scrollY,
			};

			const canvas = await html2canvas(element, html2canvasOptions);
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const margin = 10;
			const imageWidth = pageWidth - margin * 2;
			const imageHeight = (canvas.height * imageWidth) / canvas.width;
			const pageContentHeight = pageHeight - margin * 2;
			const pageCanvasHeight = (pageContentHeight * canvas.width) / imageWidth;
			let renderedHeight = 0;
			let pageIndex = 0;

			while (renderedHeight < canvas.height) {
				const sliceHeight = Math.min(pageCanvasHeight, canvas.height - renderedHeight);
				const pageCanvas = document.createElement('canvas');
				const pageContext = pageCanvas.getContext('2d');

				pageCanvas.width = canvas.width;
				pageCanvas.height = sliceHeight;

				if (!pageContext) {
					break;
				}

				pageContext.drawImage(canvas, 0, renderedHeight, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

				const imageData = pageCanvas.toDataURL('image/jpeg');
				const sliceImageHeight = Math.min(imageHeight - pageIndex * pageContentHeight, pageContentHeight);

				if (pageIndex > 0) {
					doc.addPage();
				}

				doc.addImage(imageData, 'JPEG', margin, margin, imageWidth, sliceImageHeight);

				renderedHeight += sliceHeight;
				pageIndex += 1;
			}

			doc.save(`heatmap_${current_facility?.name ?? 'report'}.pdf`);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error generating PDF:', error); // keeping for easy error troubleshooting
		} finally {
			setPrinting(false);
		}
	};

	return (
		<div className='relative min-h-screen overflow-hidden' id='overview'>
			{printing && (
				<PrintableReportHeader>
					<p className='text-[2em] font-bold leading-tight text-bluegray-900'>Heatmap</p>
					<p className='mt-2 text-[1.35em] font-medium text-bluegray-500'>{`${format(
						dateRange.applied.startDate,
						'M/d/yyyy'
					)} – ${format(dateRange.applied.endDate, 'M/d/yyyy')}`}</p>
					<p className='mt-1 text-[1.1em] font-medium text-bluegray-500'>{`${facilityName} - ${system?.name}`}</p>
					<p className={`mt-1 text-[1.1em] font-semibold ${in_concurrent_mode ? 'text-blue-500' : 'text-orange-500'}`}>
						{in_concurrent_mode ? 'Concurrent Mode' : 'Cumulative Minutes Mode'}
					</p>
				</PrintableReportHeader>
			)}
			{!printing && (
				<div className='flex w-full justify-between'>
					<PageHeading>
						<div>
							<div>Heatmap</div>
							<div className='text-[0.4em] w-fit font-semibold h-0'>
								{in_concurrent_mode ? (
									<div className='text-blue-500'>Concurrent Mode</div>
								) : (
									<div className='text-orange-500'>Cumulative Minutes Mode</div>
								)}
							</div>
						</div>
					</PageHeading>
					<div className='flex items-end justify-center h-fit pt-2.5'>
						<div className='mr-0.5 mb-0.5'>
							<Select
								label='View By'
								options={in_concurrent_mode ? concurrent_view_by_options : cumulative_view_by_options}
								sizeX='lg'
								value={
									dropDowns.viewBy.value === 'undefined' ? { value: 'day_of_week', label: 'Day of Week' } : dropDowns.viewBy
								}
								onChange={(selected) => {
									if (selected) {
										dropDowns.update({
											...dropDowns,
											viewBy: selected,
										});
										saveDropdown({
											...dropDowns,
											viewBy: selected,
										});
									}
								}}
							/>
						</div>

						<Button
							sizeX='square'
							sizeY='sm'
							variant={'primary-ghost'}
							className='rounded-none mr-4 mb-0.5 border-gray-600 border text-gray-500'
							onClick={() => {
								if (dropDowns.sortBy.value === 'undefined') {
									dropDowns.update({
										...dropDowns,
										sortBy: { value: 'descending', label: 'Descending' },
									});
									saveDropdown({
										...dropDowns,
										sortBy: { value: 'descending', label: 'Descending' },
									});
								} else if (dropDowns.sortBy.value === 'descending') {
									dropDowns.update({
										...dropDowns,
										sortBy: { value: 'ascending', label: 'Ascending' },
									});
									saveDropdown({
										...dropDowns,
										sortBy: { value: 'ascending', label: 'Ascending' },
									});
								} else {
									dropDowns.update({
										...dropDowns,
										sortBy: { value: 'undefined', label: 'None' },
									});
									saveDropdown({
										...dropDowns,
										sortBy: { value: 'undefined', label: 'None' },
									});
								}
							}}
						>
							{dropDowns.sortBy.value === 'descending' && <span className='material-symbols-outlined'>arrow_downward</span>}

							{dropDowns.sortBy.value === 'ascending' && <span className='material-symbols-outlined'>arrow_upward</span>}

							{dropDowns.sortBy.value === 'undefined' && <span className='material-symbols-outlined'>swap_vert</span>}
						</Button>

						<div className='flex text-center bg-blue-500 items-center justify-center h-10 rounded-md mr-2'>
							<span className='text-b3 font-semibold px-6 text-white'>
								{`${format(dateRange.applied.startDate, 'M/d/yyyy')} - ${format(dateRange.applied.endDate, 'M/d/yyyy')}`}
							</span>
						</div>
						<div>
							<Tooltip content={'Actual Cases'}>
								<Button
									sizeX='square'
									sizeY='md'
									variant={showHeatmap.includes('actual') ? 'primary' : 'primary-ghost'}
									className='border-r-0 rounded-none'
									onClick={() => updateShowHeatmap('actual')}
								>
									<span className='material-symbols-outlined'>history</span>
								</Button>
							</Tooltip>
							<Tooltip content={'Scheduled Cases'}>
								<Button
									sizeX='square'
									sizeY='md'
									disabled={!has_scheduled}
									variant={showHeatmap.includes('scheduled') ? 'primary' : 'primary-ghost'}
									className='border-r-0 border-l-[0.01em] rounded-none'
									onClick={() => updateShowHeatmap('scheduled')}
								>
									<span className='material-symbols-outlined'>schedule</span>
								</Button>
							</Tooltip>
							<Tooltip content={'Actual/Scheduled Difference'}>
								<Button
									sizeX='square'
									sizeY='md'
									disabled={!has_scheduled}
									variant={showHeatmap.includes('difference') ? 'primary' : 'primary-ghost'}
									className='border-r-0 border-l-[0.01em] rounded-none'
									onClick={() => updateShowHeatmap('difference')}
								>
									<span className='material-symbols-outlined'>compare</span>
								</Button>
							</Tooltip>
							<Tooltip content={'Forecast'}>
								<Button
									sizeX='square'
									disabled
									sizeY='md'
									variant={showHeatmap.includes('predicted') ? 'primary' : 'primary-ghost'}
									className='mr-2 rounded-none border-l-[0.01em]'
									onClick={() => updateShowHeatmap('predicted')}
								>
									<span className='material-symbols-outlined'>robot_2</span>
								</Button>
							</Tooltip>
						</div>
						<Drawer
							metadata={metadata}
							filtersAreDirty={filtersAreDirty}
							trigger={
								<Button sizeX='sm' sizeY='md' variant={'primary-ghost'} className='mr-2'>
									<span className='material-symbol'>filter_alt</span>
									Filters
								</Button>
							}
							quickActions={[
								{
									icon: 'undo',
									onClick: resetFilters,
									tooltipText: 'Discard unapplied filter changes',
									disabled: !filtersAreDirty,
								},
								{
									icon: 'restart_alt',
									onClick: clearFilters,
									tooltipText: 'Reset filters to default',
									disabled: !metadata.saved_at,
								},
							]}
							actionButtons={[
								{
									onClick: applyFilters,
									children: 'Apply',
									disabled: !filtersAreDirty,
								},
								{
									// eslint-disable-next-line @typescript-eslint/no-empty-function
									onClick: () => {},
									children: 'Views',
									disabled: false,
								},
							]}
						>
							<FilterFields
								fields={[
									'dateRange',
									'daysOfWeek',
									'rooms',
									'serviceLines',
									'addOns',
									'encounterTypes',
									'surgeons',
									'percentile',
									'heatmapMethod',
									'turnoverConcurrency',
									'wheelsTurnoverTimeThreshold',
									'procedures',
								]}
								dateRangeLimit={1}
								extendEndDate={has_scheduled}
							/>
						</Drawer>
						<div>
							<ButtonStack>
								<Button
									sizeX='md'
									sizeY='md'
									variant='primary'
									onClick={() => {
										setExportCsv(true);

										// setting back to false, if user clicks again
										setTimeout(() => {
											setExportCsv(false);
										}, 1000);
									}}
								>
									Export CSV
								</Button>
								<Button
									sizeX='md'
									sizeY='md'
									variant='primary'
									onClick={() => {
										setPrinting(true);
										setTimeout(() => {
											generatePDF();
										}, 0);
									}}
								>
									Export PDF
								</Button>
							</ButtonStack>
						</div>
					</div>
				</div>
			)}

			{/* <Panel
				title='Concurrent Rooms Running'
				tooltipContent={
					'Use this visualization to see how many rooms are running concurrently by hour of day and day of week. This can be helpful in determining the number of blocks, staff resources, or anesthesiology resources required during any point in the day.'
				}
				goToHelpID='heatmap'
				headerContentRight={
					
				}
			> */}
			<div className='flex mb-4 gap-4 justify-start w-full'>
				{showHeatmap.slice(0, 2).map((option, i) => (
					<MinimalHeatmap
						key={i}
						title={styleMap[option as unknown as keyof typeof styleMap].title}
						type={option}
						export_csv={exportCsv}
						showFullYAxisName={showHeatmap.length === 1 ? true : undefined}
						customPadding={showHeatmap.length === 1 ? 4 : undefined}
						colorScheme={styleMap[option as unknown as keyof typeof styleMap].colorScheme as heatmapColorSchemes}
					/>
				))}
			</div>
			<div className='flex gap-4 justify-start'>
				{showHeatmap.slice(2, 4).map((option, i) => (
					<MinimalHeatmap
						key={i}
						title={styleMap[option as unknown as keyof typeof styleMap].title}
						type={option}
						showFullYAxisName={showHeatmap.length === 1 ? true : undefined}
						customPadding={showHeatmap.length === 1 ? 4 : undefined}
						export_csv={exportCsv}
						colorScheme={styleMap[option as unknown as keyof typeof styleMap].colorScheme as heatmapColorSchemes}
					/>
				))}
			</div>

			{/* </Panel> */}
		</div>
	);
}

export function MinimalHeatmap(props: {
	type?: string;
	title: string;
	colorScheme: heatmapColorSchemes;
	export_csv?: boolean;
	showFullYAxisName?: boolean;
	customPadding?: number;
	filter_id?: number;
}) {
	const { selectedFacility } = useAppSelector((state) => state.userState);
	// Filters
	const {
		dateRange,
		daysOfWeek,
		rooms,
		addOns,
		serviceLines,
		heatmapMethod,
		encounterTypes,
		surgeons,
		percentile,
		turnoverConcurrency,
		wheelsTurnoverTimeThreshold,
		currentPageLoaded,
		dropDowns,
		filtersAreFetching,
		procedures,
	} = useFilters();

	// There is sometimes a delay in our filters when a user switches pages
	// (which is why we check if currentPageLoaded is equal to our current page),
	// To account for the delay, we tell our RTK Query to skip until we set skipRequest to false.
	const [skipRequest, setSkipRequest] = useState(true);
	useEffect(() => {
		setTimeout(() => {
			if (currentPageLoaded === '/heatmap' || currentPageLoaded === '/heatmap-compare') {
				setSkipRequest(false);
			}
		}, 0);
	}, [currentPageLoaded]);

	const requestObject =
		props.filter_id !== undefined
			? { facility_id: selectedFacility, filter_id: props.filter_id, type: props.type }
			: {
					facility_id: selectedFacility,
					viewBy: dropDowns.viewBy.value === 'undefined' ? 'day_of_week' : dropDowns.viewBy.value,
					sortBy: dropDowns.sortBy.value,
					filters: {
						days_of_week: daysOfWeek?.applied,
						rooms: rooms?.applied,
						add_ons: addOns?.applied,
						service_lines: serviceLines?.applied,
						surgeons: surgeons?.applied,
						encounter_types: encounterTypes?.applied,
						start_date: format(dateRange?.applied.startDate, 'M/d/yyyy'),
						end_date: format(dateRange?.applied.endDate, 'M/d/yyyy'),
						percentile: percentile?.applied,
						heatmap_method: heatmapMethod?.applied.name,
						turnover_concurrency: turnoverConcurrency?.applied,
						wheels_turnover_threshold: wheelsTurnoverTimeThreshold?.applied,
						procedures: procedures?.applied.map((procedure) => procedure.name),
					},
					type: props.type,
			  };

	const {
		data: roomsRunningOverview,
		isFetching,
		isError,
	} = useGetRoomsRunningQuery(requestObject, {
		skip: skipRequest || filtersAreFetching,
	});

	const isSoloHeatmap = props.customPadding !== undefined && props.showFullYAxisName !== undefined;

	// preprocess the data for exporting
	const [exportData, setExportData] = useState<{ day_of_week?: string | undefined }[] | undefined>([]);

	useEffect(() => {
		const tempexportData = roomsRunningOverview?.data.map((group) => {
			const rooms_running_values = { [dropDowns.viewBy.value]: group.y };
			const hour_of_day_values = group.x.map((v, index) => {
				return v;
			});
			Object.assign(rooms_running_values, hour_of_day_values);
			return rooms_running_values;
		});
		setExportData(tempexportData);
	}, [dropDowns.viewBy.value, roomsRunningOverview?.data]);

	useEffect(() => {
		if (props.export_csv) {
			createCSV(exportData ?? [], 'heatmap_' + props.type + '_');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.export_csv]);

	return (
		<div className={isSoloHeatmap ? `w-full` : 'w-1/2'}>
			{isFetching || skipRequest || filtersAreFetching ? (
				<div className='bg-gray-50 flex rounded-md mt-5 h-60'>
					<div className='m-auto w-full flex justify-center'>
						<LoadingIndicator />
					</div>
				</div>
			) : (roomsRunningOverview?.data.length === 0 || !roomsRunningOverview?.data) &&
			  !(isFetching || skipRequest || filtersAreFetching) ? (
				<div className='bg-gray-50 flex mt-5 h-60 rounded-md'>
					<div className='m-auto w-full flex justify-center text-gray-300'>No Data</div>
				</div>
			) : (
				roomsRunningOverview?.data && (
					<>
						<Heatmap
							title={props.title}
							showFullYAxisName={props.showFullYAxisName}
							colorScheme={props.colorScheme}
							customCellPadding={props.customPadding}
							datumFormat={(datum) => String(Math.round(parseFloat(String(datum)))).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
							axisLabels={{ x: 'Hour of Day', y: 'Day of Week' }}
							data={isError ? [] : roomsRunningOverview?.data ?? []}
							emptyMessage='This heatmap has no data to display based on the provided filters.'
						/>
					</>
				)
			)}
		</div>
	);
}

export { HeatmapPage as Heatmap, HeatmapPage as default };
